import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

const TYPE_LABELS: Record<string, string> = { uretim_merkezi: "Merkez", uretim_hatti: "Hat", unite: "Ünite", makina: "Makina" };

interface Props {
  open: boolean;
  source: { id: string; name: string; code: string; type: string } | null;
  onClose: () => void;
}

interface LocRow { id: string; code: string; name: string; type: string; parentId: string | null; }

interface PreviewNode { id: string; parentId: string; name: string; type: string; depth: number; }

type CopyMode = "merge" | "replace";

interface DoneInfo {
  mode: CopyMode;
  nodeCount: number;
  mergedCount: number;
  attrCount: number;
  attrUpdatedCount: number;
  replacedCount: number;
  repointedFaults: number;
  orphanedFaults: number;
}

/**
 * Bir hattın/merkezin ünite+makina ağacını (özellikleriyle) başka bir hatta/merkeze kopyalar.
 * - Ağaçtan SEÇME: yalnız işaretli düğümler kopyalanır (üst zincir otomatik dahil).
 * - Birleştir (merge): ad+tip eşleşen düğüm silinmez, özellikleri kaynaktan güncellenir;
 *   hedefin fazlası korunur, arıza geçmişi etkilenmez.
 * - Sil ve değiştir (replace): hedefin mevcut ekipmanı silinip seçili ağaçla değiştirilir.
 * Kodlar hedefin koduna göre yeniden üretilir. Süper admin + çift onay.
 */
export function CopyEquipmentDrawer({ open, source, onClose }: Props) {
  const qc = useQueryClient();
  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 30_000 });
  const copyMut = trpc.location.copyEquipmentTree.useMutation();

  const [targetId, setTargetId] = useState<string | null>(null);
  const [mode, setMode] = useState<CopyMode>("merge");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<DoneInfo | null>(null);

  useEffect(() => { if (open) { setTargetId(null); setMode("merge"); setAck1(false); setAck2(false); setErr(null); setDone(null); } }, [open, source?.id]);
  // Hedef değişince mod ve onaylar sıfırlanır (yanlış hedefe kalıntı onay olmasın).
  useEffect(() => { setMode("merge"); setAck1(false); setAck2(false); }, [targetId]);
  // Mod değişince de onaylar sıfırlanır — merge için verilen onay yıkıcı replace'e devrolmasın.
  function pickMode(m: CopyMode) { setMode(m); setAck1(false); setAck2(false); }

  const rows = (locsQ.data ?? []) as unknown as LocRow[];

  // Kaynağın ünite/makina alt ağacı (seçim + sayım).
  const preview = useMemo(() => {
    if (!source) return { nodes: [] as PreviewNode[], units: 0, machines: 0 };
    const byParent = new Map<string, LocRow[]>();
    rows.forEach((r) => { if (r.parentId) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); } });
    const nodes: PreviewNode[] = [];
    let units = 0, machines = 0;
    const walk = (pid: string, depth: number) => {
      const kids = (byParent.get(pid) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
      for (const k of kids) {
        if (k.type === "unite" || k.type === "makina") {
          nodes.push({ id: k.id, parentId: pid, name: k.name, type: k.type, depth });
          if (k.type === "unite") units++; else machines++;
          walk(k.id, depth + 1);
        }
      }
    };
    walk(source.id, 0);
    return { nodes, units, machines };
  }, [rows, source]);

  // Veri gelince varsayılan: hepsi seçili.
  useEffect(() => { setChecked(new Set(preview.nodes.map((n) => n.id))); }, [open, source?.id, preview.nodes.length]);

  // Seçim yardımcıları: alt dal + üst zincir haritaları.
  const childMap = useMemo(() => {
    const m = new Map<string, string[]>();
    preview.nodes.forEach((n) => { const a = m.get(n.parentId) ?? []; a.push(n.id); m.set(n.parentId, a); });
    return m;
  }, [preview.nodes]);
  const parentMap = useMemo(() => new Map(preview.nodes.map((n) => [n.id, n.parentId])), [preview.nodes]);
  const inTree = useMemo(() => new Set(preview.nodes.map((n) => n.id)), [preview.nodes]);

  function descendants(id: string): string[] {
    const out: string[] = [];
    const stack = [...(childMap.get(id) ?? [])];
    while (stack.length) { const c = stack.pop()!; out.push(c); stack.push(...(childMap.get(c) ?? [])); }
    return out;
  }
  // İşaret: düğüm + alt dalı aç/kapat; açarken üst zincir de açılır (hiyerarşi kopmaz).
  function toggleNode(id: string) {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) {
        next.delete(id);
        for (const d of descendants(id)) next.delete(d);
      } else {
        next.add(id);
        for (const d of descendants(id)) next.add(d);
        let p = parentMap.get(id);
        while (p && inTree.has(p)) { next.add(p); p = parentMap.get(p); }
      }
      return next;
    });
    setAck1(false); // seçim değişti → onay tazelensin
  }
  const allChecked = preview.nodes.length > 0 && preview.nodes.every((n) => checked.has(n.id));
  function toggleAll() {
    setChecked(allChecked ? new Set() : new Set(preview.nodes.map((n) => n.id)));
    setAck1(false);
  }

  const sel = useMemo(() => {
    let units = 0, machines = 0;
    for (const n of preview.nodes) if (checked.has(n.id)) { if (n.type === "unite") units++; else machines++; }
    return { units, machines, count: units + machines };
  }, [preview.nodes, checked]);

  // Uygun hedefler: hat/merkez, kaynak hariç.
  const targetOptions = useMemo(() => rows
    .filter((l) => (l.type === "uretim_hatti" || l.type === "uretim_merkezi") && l.id !== source?.id)
    .map((l) => ({ value: l.id, label: l.name, desc: l.code, tail: TYPE_LABELS[l.type] })), [rows, source?.id]);

  // Seçili hedefin MEVCUT ünite/makina sayısı (varsa mod seçimi gösterilir).
  const targetInfo = useMemo(() => {
    if (!targetId) return { units: 0, machines: 0 };
    const byParent = new Map<string, LocRow[]>();
    rows.forEach((r) => { if (r.parentId) { const a = byParent.get(r.parentId) ?? []; a.push(r); byParent.set(r.parentId, a); } });
    let units = 0, machines = 0;
    const walk = (pid: string) => { for (const k of (byParent.get(pid) ?? [])) { if (k.type === "unite" || k.type === "makina") { if (k.type === "unite") units++; else machines++; walk(k.id); } } };
    walk(targetId);
    return { units, machines };
  }, [rows, targetId]);
  const targetHasEquip = targetInfo.units + targetInfo.machines > 0;

  const canCopy = !!targetId && ack1 && ack2 && sel.count > 0 && !copyMut.isPending;

  async function copy() {
    if (!source || !targetId) return;
    setErr(null);
    try {
      const res = await copyMut.mutateAsync({
        sourceId: source.id,
        targetId,
        mode: targetHasEquip ? mode : "merge",
        selectedIds: allChecked ? undefined : [...checked],
      });
      setDone(res as DoneInfo); // önce done ekranı (refetch beklerken buton aktif kalıp çift tetiklenmesin)
      await qc.invalidateQueries();
    } catch (e) { setErr((e as Error).message); }
  }

  const modeBoxStyle = (active: boolean, tone: "info" | "danger") => ({
    display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", padding: "10px 12px",
    border: `1px solid ${active ? `var(--${tone})` : "var(--neutral-200)"}`,
    borderRadius: "var(--radius-md)",
    background: active ? `var(--${tone}-subtle)` : "var(--surface, #fff)",
  } as const);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Ekipman Ağacını Kopyala"
      subtitle={source ? `Kaynak: ${TYPE_LABELS[source.type] ?? ""} ${source.name} (${source.code})` : undefined}
      footer={
        done ? (
          <button type="button" className="btn-md primary" onClick={onClose}>Kapat</button>
        ) : (
          <>
            <button type="button" className="btn-md ghost" onClick={onClose} disabled={copyMut.isPending}>İptal</button>
            <button type="button" className="btn-md primary" onClick={copy} disabled={!canCopy}>
              <Icon name="copy" size={14} />{copyMut.isPending ? "Kopyalanıyor…" : mode === "replace" && targetHasEquip ? "Sil ve Değiştir" : "Kopyala"}
            </button>
          </>
        )
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {done ? (
        <div className="d-card" style={{ padding: 16, textAlign: "center" }}>
          <div style={{ color: "var(--success)", marginBottom: 8 }}><Icon name="check" size={28} /></div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{done.mode === "replace" ? "Değiştirildi" : done.mergedCount > 0 ? "Birleştirildi" : "Kopyalandı"}</div>
          <div style={{ fontSize: 13, color: "var(--neutral-600)", marginTop: 4 }}>
            {done.replacedCount > 0 ? <>Hedefteki <b>{done.replacedCount}</b> mevcut ekipman silindi. </> : null}
            <b>{done.nodeCount}</b> yeni ünite/makina kopyalandı
            {done.mergedCount > 0 ? <>, <b>{done.mergedCount}</b> mevcut ekipmanla birleştirildi</> : null}.
            {" "}Özellik: <b>{done.attrCount}</b> eklendi{done.attrUpdatedCount > 0 ? <>, <b>{done.attrUpdatedCount}</b> güncellendi</> : null}.
          </div>
          {done.repointedFaults > 0 || done.orphanedFaults > 0 ? (
            <div style={{ fontSize: 12, color: "var(--neutral-500)", marginTop: 6 }}>
              Arıza geçmişi: <b>{done.repointedFaults}</b> kayıt eşleşen yeni ekipmana taşındı{done.orphanedFaults > 0 ? <>, <b style={{ color: "var(--warning)" }}>{done.orphanedFaults}</b> kayıt (yeniden oluşmayan ekipman) ekipman bağlantısını kaybetti</> : null}.
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {/* Seçimli önizleme */}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)" }}>
              Kopyalanacak Ağaç · seçili {sel.units} ünite, {sel.machines} makina
            </span>
            {preview.nodes.length > 0 ? (
              <button type="button" className="btn" style={{ height: 22, padding: "0 8px", fontSize: 11 }} onClick={toggleAll}>
                {allChecked ? "Tümünü Bırak" : "Tümünü Seç"}
              </button>
            ) : null}
          </div>
          {preview.nodes.length === 0 ? (
            <div style={{ padding: 12, color: "var(--warning)", fontSize: 13 }}>Bu kaynakta kopyalanacak ünite/makina yok.</div>
          ) : (
            <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", maxHeight: 200, overflowY: "auto", padding: 4, marginBottom: 4 }}>
              {preview.nodes.map((n) => (
                <label key={n.id} htmlFor={`cpsel-${n.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 8px", paddingLeft: 8 + n.depth * 16, fontSize: 13, cursor: "pointer", opacity: checked.has(n.id) ? 1 : 0.5 }}>
                  <input id={`cpsel-${n.id}`} type="checkbox" checked={checked.has(n.id)} onChange={() => toggleNode(n.id)} />
                  <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", padding: "1px 5px", borderRadius: 4, color: "var(--neutral-600)", background: "var(--neutral-100)" }}>{TYPE_LABELS[n.type]}</span>
                  <span>{n.name}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: "var(--neutral-500)", marginBottom: 14 }}>
            Bir üniteyi bırakınca altındaki makinalar da bırakılır; bir makinayı seçince üst ünitesi otomatik dahil edilir.
          </div>

          {/* Hedef seçimi */}
          <div className="form-field">
            <label className="lbl">Hedef Hat / Merkez <span className="req">*</span></label>
            <SearchSelect value={targetId} options={targetOptions} onChange={setTargetId} placeholder="Hedef seç…" searchPlaceholder="Ara…" icon="mapPin" />
            <span className="help">Seçili ağaç bu hattın altına kopyalanır; yeni düğümlerin kodları hedefin koduna göre üretilir (çakışmasız).</span>
          </div>

          {/* Hedefte mevcut ekipman varsa: birleştir / sil-değiştir seçimi */}
          {targetHasEquip ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              <div style={{ fontSize: 12, color: "var(--neutral-600)" }}>
                Hedefte zaten <b>{targetInfo.units} ünite, {targetInfo.machines} makina</b> var. Nasıl davranılsın?
              </div>
              <label htmlFor="cp-mode-merge" style={modeBoxStyle(mode === "merge", "info")}>
                <input id="cp-mode-merge" type="radio" name="cp-mode" checked={mode === "merge"} onChange={() => pickMode("merge")} style={{ marginTop: 2 }} />
                <span style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: "var(--info)" }}>Birleştir (önerilen).</span>{" "}
                  Kaynakta olup hedefte olmayan ekipman ve özellikler <b>eklenir</b>; aynı ad+tipte eşleşen ekipmanın özellikleri
                  kaynaktan <b>güncellenir</b> (etiket eşleşen değer/birim güncellenir, olmayan eklenir). Hedefteki fazla ekipman ve
                  özellikler <b>korunur</b>; arıza geçmişi etkilenmez.
                </span>
              </label>
              <label htmlFor="cp-mode-replace" style={modeBoxStyle(mode === "replace", "danger")}>
                <input id="cp-mode-replace" type="radio" name="cp-mode" checked={mode === "replace"} onChange={() => pickMode("replace")} style={{ marginTop: 2 }} />
                <span style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: "var(--danger)" }}>Sil ve değiştir.</span>{" "}
                  Hedefteki <b>{targetInfo.units} ünite, {targetInfo.machines} makina</b> (ve özellikleri) <b>silinip</b> seçili kaynak
                  ağaçla değiştirilecek. Arıza geçmişi eşleşen yeni ekipmana taşınır. Bağlı stok/parti kaydı olan ekipman silinemez.
                </span>
              </label>
            </div>
          ) : null}

          {/* Çift onay (süper admin) */}
          <div style={{ borderTop: "1px solid var(--neutral-200)", paddingTop: 12, marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={ack1} onChange={(e) => setAck1(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Seçili ağacın (<b>{sel.units} ünite, {sel.machines} makina</b>) hedefe {targetHasEquip && mode === "replace" ? "mevcutları SİLEREK" : "birleştirilerek"} kopyalanacağını onaylıyorum.</span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={ack2} onChange={(e) => setAck2(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Bu işlemi <b>süper admin</b> olarak onaylıyorum. Özellikler dahil kopyalanır.</span>
            </label>
          </div>
        </>
      )}
    </Drawer>
  );
}
